import React, { useEffect, useState } from 'react'
import { LockClosedIcon, ShieldCheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { getApplicantPayment, startApplicantPayment, ApplicantPaymentError, type ApplicantAuth, type ApplicantPaymentInfo } from '../../lib/billingApi'
import { decidePaymentStep, validatePayerDetails, paymentFailureText, type PaymentReturnStatus } from '../../lib/billing'
import { formatMoney } from '../../lib/money'
import { BRAND_NAME } from '../../config/brand'

// Applicant-facing "Verification fee" step. Styled with the page's theme
// variables (--paper/--panel/--ink/--accent) so page-builder branding applies.

export interface VerificationFeeStepProps {
  verificationId: string
  auth: ApplicantAuth
  /** `?payment=` on the return URL (success | failed | pending). */
  returnStatus: PaymentReturnStatus
  /** `?reason=` on a failed return. */
  returnReason?: string | null
  /** Called once no fee is due (not required, or paid). */
  onCleared: () => void
  logoUrl?: string | null
  companyName?: string | null
}

const card: React.CSSProperties = {
  background: 'var(--panel)', color: 'var(--ink)', borderRadius: 28, padding: 28, width: '100%', maxWidth: 460,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 24px 60px -30px rgba(0,0,0,0.25)', border: '1px solid var(--rule)',
}
const input: React.CSSProperties = {
  width: '100%', height: 48, borderRadius: 16, border: '1px solid var(--rule-strong)', background: 'var(--paper)',
  color: 'var(--ink)', padding: '0 14px', fontSize: 15, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
const label: React.CSSProperties = { display: 'block', fontSize: 13, color: 'var(--mid)', margin: '0 0 6px', fontWeight: 500 }
const errText: React.CSSProperties = { fontSize: 12.5, color: 'var(--flag)', marginTop: 5 }

export function VerificationFeeStep({ verificationId, auth, returnStatus, returnReason, onCleared, logoUrl, companyName }: VerificationFeeStepProps) {
  const [payment, setPayment] = useState<ApplicantPaymentInfo | null>(null)
  const [fetchFailed, setFetchFailed] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const load = async () => {
    try {
      setPayment(await getApplicantPayment(verificationId, auth))
      setFetchFailed(false)
    } catch {
      setFetchFailed(true)
    }
  }

  useEffect(() => { load() }, [verificationId])

  const decision = decidePaymentStep({ payment, fetchFailed, returnStatus, confirmAttempts: attempts })

  useEffect(() => { if (decision.step === 'clear') onCleared() }, [decision.step])

  // After a success / pending return the server may lag the redirect — re-check a few times.
  useEffect(() => {
    if (decision.step !== 'confirming') return
    const t = setTimeout(async () => { await load(); setAttempts(a => a + 1) }, attempts === 0 ? 600 : 2500)
    return () => clearTimeout(t)
  }, [decision.step, attempts])

  const errors = validatePayerDetails({ name, email, phone })
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (Object.keys(errors).length) return
    setBusy(true)
    setSubmitError(null)
    try {
      // return_url = this exact page (keeps ?session=… and the other params)
      const { checkout_url } = await startApplicantPayment(verificationId, auth, { name, email, phone }, window.location.href)
      window.location.assign(checkout_url)
    } catch (err) {
      if (err instanceof ApplicantPaymentError && err.code === 'payment_not_required') { onCleared(); return }
      setSubmitError(err instanceof Error ? err.message : 'Could not start the payment')
      setBusy(false)
    }
  }

  const org = payment?.organisation_name || companyName || 'This organisation'
  const wrap = (children: React.ReactNode) => (
    <div style={{ background: 'var(--paper)', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, gap: 24, fontFamily: 'var(--sans)' }}>
      {logoUrl
        ? <img src={logoUrl} alt={companyName || 'Logo'} style={{ height: 34, objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
        : <img src="/saflens-logo.png" alt={BRAND_NAME} style={{ height: 30 }} />}
      {children}
    </div>
  )

  if (decision.step === 'checking' || decision.step === 'confirming' || decision.step === 'clear') {
    return wrap(
      <div style={{ ...card, textAlign: 'center', padding: '40px 28px' }} aria-live="polite">
        <div style={{ width: 52, height: 52, borderRadius: 99, margin: '0 auto 14px', display: 'grid', placeItems: 'center', background: 'var(--accent-soft)', color: 'var(--accent)' }}>
          <ArrowPathIcon style={{ width: 24, height: 24, animation: 'sf-spin 1s linear infinite' }} />
        </div>
        <div style={{ fontWeight: 600, fontSize: 17 }}>{decision.step === 'confirming' ? 'Confirming your payment…' : 'Preparing your verification…'}</div>
        <div style={{ color: 'var(--mid)', fontSize: 14, marginTop: 6 }}>{decision.step === 'confirming' ? 'This usually takes a few seconds.' : 'Just a moment.'}</div>
        <style>{'@keyframes sf-spin { to { transform: rotate(360deg) } }'}</style>
      </div>,
    )
  }

  return wrap(
    <form style={card} onSubmit={submit} noValidate>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 12px', borderRadius: 99, background: 'var(--accent-soft)', color: 'var(--accent-ink)', fontSize: 12.5, fontWeight: 600 }}>
          <ShieldCheckIcon style={{ width: 15, height: 15 }} /> Verification fee
        </span>
        <span style={{ fontSize: 13, color: 'var(--mid)' }}>Step 1 of 2</span>
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', margin: '18px 0 6px' }}>{org} requires a fee</h1>
      <p style={{ margin: 0, color: 'var(--mid)', fontSize: 14.5, lineHeight: 1.5 }}>
        {payment?.description || 'Pay the identity verification fee to continue. You’ll come straight back here to finish your check.'}
      </p>

      <div style={{ margin: '20px 0', padding: '18px 20px', borderRadius: 20, background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, opacity: 0.9 }}>Amount due</span>
        <span style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(payment?.amount_cents ?? 0)} <small style={{ fontSize: 13, fontWeight: 500, opacity: 0.85 }}>{payment?.currency || 'USD'}</small>
        </span>
      </div>

      {decision.notice === 'payment_failed' && (
        <div role="alert" style={{ borderRadius: 16, padding: '12px 14px', background: 'var(--flag-soft)', color: 'var(--flag)', fontSize: 13.5, marginBottom: 16 }}>
          {paymentFailureText(returnReason ?? null)}
        </div>
      )}
      {decision.notice === 'payment_pending' && (
        <div role="status" style={{ borderRadius: 16, padding: '12px 14px', background: 'var(--accent-soft)', color: 'var(--accent-ink)', fontSize: 13.5, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Your payment is still being confirmed. If you've already paid, refresh in a moment rather than paying again.</span>
          <button type="button" onClick={() => { setAttempts(0); load() }} style={{ border: 0, background: 'var(--panel)', color: 'var(--ink)', borderRadius: 99, padding: '7px 12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>Check again</button>
        </div>
      )}

      <div style={{ display: 'grid', gap: 14 }}>
        <div>
          <label style={label} htmlFor="fee-name">Full name</label>
          <input id="fee-name" style={{ ...input, ...(touched && errors.name ? { borderColor: 'var(--flag)' } : {}) }} autoComplete="name" value={name} onChange={e => setName(e.target.value)} placeholder="As shown on your ID" />
          {touched && errors.name && <div style={errText}>{errors.name}</div>}
        </div>
        <div>
          <label style={label} htmlFor="fee-email">Email</label>
          <input id="fee-email" type="email" style={{ ...input, ...(touched && errors.email ? { borderColor: 'var(--flag)' } : {}) }} autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
          {touched && errors.email && <div style={errText}>{errors.email}</div>}
        </div>
        <div>
          <label style={label} htmlFor="fee-phone">Phone <span style={{ color: 'var(--soft)' }}>(optional, for mobile money)</span></label>
          <input id="fee-phone" type="tel" style={{ ...input, ...(touched && errors.phone ? { borderColor: 'var(--flag)' } : {}) }} autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+254 700 000 000" />
          {touched && errors.phone && <div style={errText}>{errors.phone}</div>}
        </div>
      </div>

      {submitError && <div role="alert" style={{ ...errText, fontSize: 13.5, marginTop: 14 }}>{submitError}</div>}

      <button type="submit" disabled={busy} style={{
        marginTop: 20, width: '100%', height: 52, borderRadius: 99, border: 0, background: 'var(--ink)', color: 'var(--paper)',
        fontSize: 16, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        opacity: busy ? 0.7 : 1, fontFamily: 'inherit',
      }}>
        <LockClosedIcon style={{ width: 18, height: 18 }} />
        {busy ? 'Opening secure checkout…' : `Pay ${formatMoney(payment?.amount_cents ?? 0)}`}
      </button>
      <p style={{ margin: '14px 0 0', textAlign: 'center', color: 'var(--soft)', fontSize: 12.5 }}>
        Secure checkout by Flutterwave · card or mobile money
      </p>
    </form>,
  )
}
